import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import {
  createCourse,
  fetchCourses,
  setCourseActive,
  updateCourse,
  type Course,
} from '@/features/masters/api';
import { ApiError } from '@/lib/api/http';

/**
 * 業務マスター > コース（D-114）。
 * 期と並ぶもう一方の軸。会員は「期」と「コース」の2つで束ねる。
 * 画面の作りは期マスタと同じ（削除は物理削除せず「無効化」・使用中件数を出す）。
 */
export function CoursesPage(): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['courses'], queryFn: fetchCourses });

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  async function onCreate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await createCourse({ name: newName });
      setNewName('');
      await queryClient.invalidateQueries({ queryKey: ['courses'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveName(id: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await updateCourse(id, { name: editingName });
      setEditingId(null);
      await queryClient.invalidateQueries({ queryKey: ['courses'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onToggleActive(course: Course): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setCourseActive(course.id, !course.is_active);
      await queryClient.invalidateQueries({ queryKey: ['courses'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<Course>[] = [
    { key: 'code', header: 'コード', render: (c) => c.code },
    {
      key: 'name',
      header: '名称',
      primary: true,
      render: (c) =>
        editingId === c.id ? (
          <input
            autoFocus
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void onSaveName(c.id);
              if (e.key === 'Escape') setEditingId(null);
            }}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
        ) : (
          c.name
        ),
    },
    {
      key: 'member_count',
      header: '使用中件数',
      render: (c) => `${c.member_count}件`,
    },
    {
      key: 'is_active',
      header: '状態',
      render: (c) => (c.is_active ? '有効' : '無効化'),
    },
  ];

  return (
    <>
      <PageHeader
        title="コース"
        description="受講コースを登録します。ここに登録すると、会員登録の「コース」の選択肢、会員CSVの「コースコード」、イベントの対象者抽出の条件として使えるようになります。期と組み合わせて会員を束ねます。"
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <div className="mb-4 rounded-sm border border-wpborder bg-white px-3 py-3">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void onCreate();
          }}
        >
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">名称</span>
            <input
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            コースを追加
          </button>
        </form>
      </div>

      <ListTable<Course>
        caption="コースマスタ一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(c) => c.id}
        state={state}
        errorMessage={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
        rowActions={[
          {
            label: '名称を編集',
            onSelect: (c: Course) => {
              setEditingId(c.id);
              setEditingName(c.name);
            },
          },
          {
            label: '無効化・有効化',
            onSelect: (c: Course) => void onToggleActive(c),
          },
        ]}
        emptyTitle="コースが登録されていません"
      />
    </>
  );
}
